import { useEffect } from 'react';
import {
    json,
    type MetaDescriptor,
    type LoaderFunctionArgs,
} from '@remix-run/node';
import {
    Outlet,
    useLoaderData,
    useLocation,
    useMatches,
    useNavigate,
    type UIMatch,
} from '@remix-run/react';
import { getPaginationOpts } from '~/modules/root/util/get-pagination-opts.server';
import { listDigitalMagazines } from '@modules/magazine/api';
import { PaginatedMagazines } from '@modules/magazine/component/paginated-magazines/PaginatedMagazines';
import { HeaderSection } from '@modules/magazine/component/header-section/HeaderSection';
import {
    createMetaTitle,
    createMetaDescription,
    createOGTags,
    getCanonicalHreflang,
    mergeMeta,
} from '~/modules/seo';
import { getPreloadImageLink } from '~/modules/images';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import { digitalPublicationFilters } from '~/modules/magazine/api/digital-publication-filters.server';
import type { DigitalMagazineLoader } from '~/routes/_app.digital-magazine._layout.$urlSegment';
import type { APHandle } from '~/modules/root';
import React from 'react';
import { useCurrentCategory } from '@rocco/ui/website-header/hooks/use-current-category';

export const meta = mergeMeta<typeof loader>(
    () => {
        return [];
    },
    (props) => {
        const { data } = props;
        const title = createMetaTitle(
            props,
            [data?.pageTitle],
            true,
            data?.pagination?.page
        );
        const description = createMetaDescription([]);
        const numberTilesToPreload = data?.deviceType === 'desktop' ? 4 : 2;
        const preloadTiles =
            data?.magazineTiles
                .slice(0, numberTilesToPreload)
                .reduce((acc, tile) => {
                    if (!tile.imageSrc) return acc;
                    const imageSizes = getImagesSizes({
                        desktop: 402,
                        mobile: 188,
                    });
                    const link =
                        data?.deviceType === 'mobile'
                            ? getPreloadImageLink(
                                  undefined,
                                  tile.imageSrc,
                                  imageSizes
                              )
                            : getPreloadImageLink(
                                  tile.imageSrc,
                                  undefined,
                                  imageSizes
                              );
                    return acc.concat(link);
                }, [] as MetaDescriptor[]) ?? [];
        return [
            { title },
            { name: 'description', content: description },
            ...createOGTags({
                meta: props,
                title,
                description,
            }),
            ...getCanonicalHreflang(props),
            ...preloadTiles,
        ];
    }
);

export const loader = async ({
    context,
    request,
    params,
}: LoaderFunctionArgs) => {
    const month = params?.month;
    const year = params?.year;
    const { device } = context;
    const url = new URL(request.url);
    const pagination = getPaginationOpts({
        request,
        device,
    });

    const searchParams = url.searchParams;
    const optionKey = searchParams.get('sort') ?? undefined;

    const searchYear = searchParams.get('year');
    const searchMonth = searchParams.get('month');

    const [digitalMagazines, filters] = await Promise.all([
        listDigitalMagazines({
            ...context,
            params: url.searchParams,
            filters: {
                year: searchYear ?? year,
                month: searchMonth ?? month,
                optionKey,
            },
        }),
        digitalPublicationFilters(context),
    ]);

    const totalPages = digitalMagazines.totalPages;

    return json({
        magazineTiles: digitalMagazines.magazines,
        pagination: {
            totalCount: digitalMagazines.totalCount,
            page: pagination.page,
            prevPage: pagination.prevPage,
            nextPage: pagination.nextPage,
            offset: pagination.offset,
            pageSize: pagination.limit,
            totalPages,
            isFirstPage: pagination.prevPage === 0,
            isLastPage: pagination.page >= totalPages,
            currentRange: {
                from: digitalMagazines.itemRange.from,
                to: digitalMagazines.itemRange.to,
            },
        },
        totalCount: digitalMagazines.totalCount,
        breadcrumb: digitalMagazines.breadcrumb,
        pageTitle: digitalMagazines.pageTitle,
        description: digitalMagazines.description,
        deviceType: device.type,
        filterState: digitalMagazines.filterState,
        filters,
        year,
        month,
    });
};

const DigitalMagazinePage = () => {
    const {
        magazineTiles,
        pagination,
        totalCount,
        breadcrumb,
        pageTitle,
        description,
        filterState,
        filters,
    } = useLoaderData<typeof loader>();
    const matches = useMatches();
    const location = useLocation();
    const navigate = useNavigate();

    const { setCategory } = useCurrentCategory();

    // Set the current category for header navigation
    React.useEffect(() => {
        setCategory(null);
    }, [setCategory]);

    const routeMatch = matches.find(
        (match) => match.handle && match.handle.hasOwnProperty('isDialogRoute')
    ) as UIMatch<DigitalMagazineLoader, APHandle> | undefined;

    const isDialogRoute = routeMatch?.handle?.isDialogRoute;

    useEffect(() => {
        const isLegacyFragmentURL = location?.hash?.startsWith('#/view');
        if (isLegacyFragmentURL) {
            navigate(`/digital-magazine/${location?.hash?.split('/').pop()}`, {
                replace: true,
            });
        }
    }, [location, navigate]);

    return (
        <div>
            <div className="mx-auto max-w-screen-qhd pb-20 md:px-12.5 md:pb-32.5 qhd:px-0">
                <HeaderSection
                    title={pageTitle}
                    description={description}
                    itemsCount={totalCount}
                    breadcrumb={breadcrumb}
                    initialFilterState={filterState}
                    filters={filters}
                />
                <PaginatedMagazines
                    magazineTiles={magazineTiles}
                    initialFilterState={filterState}
                    pagination={pagination}
                    filters={filters}
                />
            </div>

            {isDialogRoute && <Outlet />}
        </div>
    );
};
export default DigitalMagazinePage;
